import React, { useContext, useEffect, useRef, useState } from 'react';

import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon, CloseIcon } from '$partials/icon';
import { ModalContext } from '$contexts/modal.jsx';
import { Button, Input } from '$partials/ui';
import { Collapsible, CollapsibleContent, CollapsibleContextConsumer, CollapsibleHeading } from '$partials/base/collapsible.jsx';
import { useCursorContext } from '$contexts/cursor.jsx';
import { calculateStampDuty } from '$utilities/data';
import { formatNumber, formatPrice } from '$utilities/format';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').StampDutyModal}
 */
const StampDutyModal = ({
    description,
    brackets = [{
        leftCol: '0—£125,000',
        rightCol: '0%'
    },
    {
        leftCol: '£125,001—£250,000',
        rightCol: '2%'
    },
    {
        leftCol: '£250,001—£925,000',
        rightCol: '5%'
    },
    {
        leftCol: '£925,001—£1.5 million',
        rightCol: '10%'
    },
    {
        leftCol: 'Above £1.5 million',
        rightCol: '12%'
    }],
    footnote,
    priceGbp,
    tenure,
    leaseLength,
    groundRent,
}) => {
    const { handleModal } = useContext(ModalContext);
    const { dispatch: cursorDispatch } = useCursorContext();
    const exitButtonRef = useRef(null);

    const [ checkedValues, setCheckedValues ] = useState({
        isNotResident: false,
        isFirstHome: false,
        isSecondHome: false
    });
    const [ inputValue, setInputValue ] = useState(formatNumber(priceGbp));
    const [results, setResults] = useState(null);

    useEffect(() => {
        const exit = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: exitButtonRef,
                cursorType: 'exit'
            },
        });

        return () => [exit].map(cleanup => {
            cleanup();});
    }, []);

    return (
        <>
            <div
                className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-black mix-blend-multiply z3 cursor-none'}
                onClick={() => handleModal()}
                ref={exitButtonRef}
                style={{opacity: '0.2'}}>
            </div>
            <div
                className='height-100 bg-white z4 fixed top-0 overflow-auto modal-container'>
                <button
                    aria-label='Close'
                    className='form-field-reset link-reset absolute right-0 right mt6 mr6 cursor-pointer'
                    onClick={() => handleModal()}
                    type='button'>
                    <CloseIcon/>
                </button>
                <div className='py7 px5 md-px6'>
                    <h2 className='type-style-2'>Stamp Duty</h2>
                    <div
                        className='pt5 pb7 type-style-6'
                        dangerouslySetInnerHTML={{__html: description}} />

                    <Input
                        className='type-style-3'
                        label='Total Purchase Sum'
                        onChange={(e)=> {
                            setInputValue(formatNumber(e.currentTarget.value.replaceAll(',', '')));}
                        }
                        renderIcon={<h3 className='type-style-3'>£</h3>}
                        value={inputValue}/>

                    <div className='flex flex-col pt6 pb6 md-pt7 sm-pb7'>
                        <div className='flex checkbox items-center relative'>
                            <input
                                checked={checkedValues.isNotResident}
                                id='isNotResident'
                                name='isNotResident'
                                onChange={() => setCheckedValues(prev => ({...checkedValues, isNotResident: !prev.isNotResident}))}
                                type='checkbox'
                                value='isResident'/>
                            <label
                                className='type-style-6'
                                htmlFor='isNotResident'>The buyer is a non-UK resident</label>
                        </div>
                        <div className='flex checkbox items-center relative'>
                            <input
                                checked={checkedValues.isFirstHome}
                                id='isFirstHome'
                                name='isFirstHome'
                                onChange={() => setCheckedValues(prev => ({...checkedValues, isFirstHome: !prev.isFirstHome}))}
                                type='checkbox'
                                value='isFirstHome'/>
                            <label
                                className='type-style-6'
                                htmlFor='isFirstHome'>Property is a first home</label>
                        </div>
                        <div className='flex checkbox items-center relative'>
                            <input
                                checked={checkedValues.isSecondHome}
                                id='isSecondHome'
                                name='isSecondHome'
                                onChange={() => setCheckedValues(prev => ({...checkedValues, isSecondHome: !prev.isSecondHome}))}
                                type='checkbox'
                                value='isSecondHome'/>
                            <label
                                className='type-style-6'
                                htmlFor='isSecondHome'>Property is a buy to let or second home*</label>
                        </div>
                        <div className='ml7'>
                            <p
                                className='type-style-7'>
                                {'*Buy-to-let/second home rates are 5 percentage points above the current SDLT (Stamp Duty Land Tax). You may be eligible for claiming this back or not paying this at all. Please gain independent advice or ask us to refer you to a suitable advisor.'}
                            </p>
                        </div>
                    </div>

                    {/* CTA button */}
                    <Button
                        as = 'button'
                        className = 'bg-black white type-style-5'
                        look = 'fill'
                        onClick={() => {
                            const {totalDuty, taxableSum} = calculateStampDuty({
                                priceGbp: inputValue.replaceAll(',', ''),
                                tenure,
                                leaseLength,
                                groundRent,
                            }, checkedValues);
                            setResults({
                                stampDuty: formatPrice(totalDuty),
                                effectiveRate: inputValue ? formatNumber(totalDuty/(inputValue.replaceAll(',', '')) * 100, {maximumFractionDigits: 2}) : formatNumber(0),
                                taxableSum: formatPrice(taxableSum)
                            });
                        }}
                        renderIcon = {<ArrowRightIcon/>}>
                                View Results
                    </Button>

                    {/* Results */}
                    {results &&
                    <>
                        <div className='flex flex-wrap mt6 md-mt7'>
                            <div className='border-top border-left border-right width-100 flex-grow px5 md-px6 pt5 pb5 md-pb7 bg-white'>
                                <p className='type-style-5 py3'>Stamp duty to pay</p>
                                <h3 className='type-style-3'>{results.stampDuty}</h3>
                            </div>
                            <div className='border-top border-left border-right md-border-0 md-border-left md-border-top md-border-bottom col-12 md-col-6 flex-grow px5 md-px6 pt5 pb5 md-pb7 bg-white'>
                                <p className='type-style-5 py3'>Effective Rate</p>
                                <h3 className='type-style-3'>{results.effectiveRate} %</h3>
                            </div>
                            <div className='border col-12 md-col-6 flex-grow px5 md-px6 pt5 pb5 md-pb7 bg-white'>
                                <p className='type-style-5 py3'>Taxable Sum</p>
                                <h3 className='type-style-3'>{results.taxableSum}</h3>
                            </div>
                        </div>
                        <div
                            className='absolute modal-y-bleed translate-yn-100 zn1'
                            style={{maxWidth: '573px;'}}>
                            <div className='bg-peach height-7'/>
                        </div>
                        <div className='bg-peach modal-full-width pb7'>
                            <div className='px5 md-px6'>
                                {/* Accordion */}
                                <Collapsible
                                    id='stamp-duty-calculator'
                                    isOpened={false}>
                                    <CollapsibleHeading className='form-field-reset width-100'>
                                        <CollapsibleContextConsumer>
                                            {({ isOpened }) => (
                                                <div
                                                    className={`inline-flex width-100 justify-between py5 md-py5 items-center bg-none type-style-5 cursor-pointer black${!isOpened ? ' border-bottom' : ''}`}>
                                                View brackets
                                                    <span
                                                        className='font-0 line-height-0 bg-white black button border-radius-100px p3 md-p5'>
                                                        {isOpened ? <ArrowUpIcon/> : <ArrowDownIcon/>}
                                                    </span>
                                                </div>
                                            )}
                                        </CollapsibleContextConsumer>
                                    </CollapsibleHeading>
                                    <CollapsibleContent className='clearfix'>
                                        {brackets.map((bracket, idx) => {
                                            return (
                                                <div
                                                    className='col-12 flex justify-between mb3'
                                                    key={`stamp-duty-bracket-${idx}`}>
                                                    <p className='type-style-6'>{bracket.leftCol}</p>
                                                    <p className='type-style-6'>{bracket.rightCol}</p>
                                                </div>
                                            );
                                        })}

                                    </CollapsibleContent>
                                </Collapsible>
                            </div>
                        </div>
                    </>
                    }

                    {/* Footnote */}
                    <div className='mt6 md-mt7 col-12 sm-col-8'>
                        <p className='type-style-6'>{footnote}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default StampDutyModal ;
